import React, { useEffect, useRef, useState } from 'react';
import './timeInput12.css';

export function convert24To12Parts(time24) {
  if (!time24) return { hour: '', minute: '', ampm: '' };
  const [hours, minutes] = String(time24).slice(0, 5).split(':').map(Number);
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return {
    hour: String(hour12),
    minute: String(minutes || 0).padStart(2, '0'),
    ampm,
  };
}

function convert12PartsTo24(hour, minute, ampm) {
  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);
  if (Number.isNaN(hourNum) || Number.isNaN(minuteNum) || !ampm) return '';

  let hour24 = hourNum;
  if (ampm === 'PM' && hourNum !== 12) hour24 += 12;
  else if (ampm === 'AM' && hourNum === 12) hour24 = 0;

  return `${String(hour24).padStart(2, '0')}:${String(minuteNum).padStart(2, '0')}`;
}

function sanitizeHour(raw) {
  return raw.replace(/\D/g, '').slice(0, 2);
}

function sanitizeMinute(raw) {
  return raw.replace(/\D/g, '').slice(0, 2);
}

function normalizeHour(raw) {
  if (!raw) return '';
  const num = parseInt(raw, 10);
  if (Number.isNaN(num) || num < 1) return '';
  if (num > 12) return '12';
  return String(num);
}

function normalizeMinute(raw) {
  if (!raw) return '';
  const num = parseInt(raw, 10);
  if (Number.isNaN(num) || num < 0) return '';
  if (num > 59) return '59';
  return String(num).padStart(2, '0');
}

export default function TimeInput12({
  value = '',
  onChange,
  className = 'form-control',
  disabled = false,
}) {
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');
  const [ampm, setAmpm] = useState('');
  const containerRef = useRef(null);
  const minuteRef = useRef(null);
  const ampmRef = useRef(null);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (isEditingRef.current) return;
    const parts = convert24To12Parts(value);
    setHour(parts.hour);
    setMinute(parts.minute);
    setAmpm(parts.ampm);
  }, [value]);

  const commitValue = (nextHour, nextMinute, nextAmpm) => {
    const normalizedHour = normalizeHour(nextHour);
    const normalizedMinute = normalizeMinute(nextMinute);

    if (normalizedHour && normalizedMinute && nextAmpm) {
      const time24 = convert12PartsTo24(normalizedHour, normalizedMinute, nextAmpm);
      if (time24) onChange?.({ target: { value: time24 } });
      return;
    }

    if (!normalizedHour && !nextMinute && !nextAmpm) {
      onChange?.({ target: { value: '' } });
    }
  };

  const finalizeEditing = () => {
    const normalizedHour = normalizeHour(hour);
    const normalizedMinute = normalizeMinute(minute);
    setHour(normalizedHour);
    setMinute(normalizedMinute);
    commitValue(normalizedHour, normalizedMinute, ampm);
  };

  const handleContainerBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    isEditingRef.current = false;
    finalizeEditing();
  };

  const handleHourChange = (raw) => {
    const next = sanitizeHour(raw);
    setHour(next);
    if (next.length === 2) {
      minuteRef.current?.focus();
    }
  };

  const handleMinuteChange = (raw) => {
    const next = sanitizeMinute(raw);
    setMinute(next);
    if (next.length === 2) {
      ampmRef.current?.focus();
    }
  };

  const handleAmpmChange = (next) => {
    setAmpm(next);
    const normalizedHour = normalizeHour(hour);
    const normalizedMinute = normalizeMinute(minute);
    if (normalizedHour && normalizedMinute && next) {
      commitValue(normalizedHour, normalizedMinute, next);
    } else if (!normalizedHour && !minute && !next) {
      onChange?.({ target: { value: '' } });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`time-input-12 ${className}${disabled ? ' time-input-12--disabled' : ''}`}
      aria-label="Time"
      onFocusCapture={() => {
        isEditingRef.current = true;
      }}
      onBlurCapture={handleContainerBlur}
    >
      <input
        type="text"
        inputMode="numeric"
        className="time-input-12__segment"
        value={hour}
        onChange={(e) => handleHourChange(e.target.value)}
        placeholder="HH"
        maxLength={2}
        disabled={disabled}
        aria-label="Hour"
      />
      <span className="time-input-12__separator" aria-hidden="true">:</span>
      <input
        ref={minuteRef}
        type="text"
        inputMode="numeric"
        className="time-input-12__segment"
        value={minute}
        onChange={(e) => handleMinuteChange(e.target.value)}
        placeholder="MM"
        maxLength={2}
        disabled={disabled}
        aria-label="Minute"
      />
      <select
        ref={ampmRef}
        className="time-input-12__ampm"
        value={ampm}
        onChange={(e) => handleAmpmChange(e.target.value)}
        disabled={disabled}
        aria-label="AM or PM"
      >
        <option value="">--</option>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
